/**
 * Rotas do jogo.
 *
 * Antes disto `App.vue` alternava telas com as flags locais `showRanking` e `showProfile`:
 * nenhuma tela tinha URL, o botão voltar saía do app, recarregar sempre caía no jogo, e as
 * três iam no mesmo chunk. Ver docs/AI_DIRETIVES/COMPONENTS.md § 2.
 */
import { createRouter, createWebHistory } from 'vue-router';
import GameView from '../views/GameView.vue';
import store from '../store';
import { WELCOME_KEY } from '../views/WelcomeView.vue';
import { firstTime as neverPickedTheme } from '../lib/theme';

const routes = [
  {
    // Boas-vindas: a primeira visita cai aqui, e só ela. `meta.standalone` tira o cabeçalho
    // e a barra de abas — a tela é a marca inteira, sem moldura de app em volta.
    path: '/bem-vindo',
    name: 'welcome',
    component: () => import(/* webpackChunkName: "conta" */ '../views/WelcomeView.vue'),
    meta: { standalone: true },
  },
  // Hoje é a porta de entrada: o que existe para jogar agora, e quem está esperando você.
  {
    path: '/hoje',
    name: 'today',
    component: () => import(/* webpackChunkName: "hoje" */ '../views/TodayView.vue'),
    meta: { label: 'Hoje', icon: 'calendar-days' },
  },
  { path: '/', name: 'game', component: GameView, meta: { label: 'Jogar', icon: 'gamepad-2' } },
  {
    // A trilha é aba, e não linha de folha: ela é um modo com casa própria — o mapa — e
    // esconder isso atrás de duas portas cobrava dois toques por partida.
    path: '/trilha',
    name: 'trail',
    component: () => import(/* webpackChunkName: "trilha" */ '../views/TrailView.vue'),
    meta: { label: 'Trilha', icon: 'flag' },
  },
  {
    path: '/duelos',
    name: 'duels',
    component: () => import(/* webpackChunkName: "duelo" */ '../views/DuelsView.vue'),
    meta: { label: 'Duelos', icon: 'swords' },
  },
  {
    path: '/duelos/entrar/:code',
    name: 'duel-join',
    component: () => import(/* webpackChunkName: "duelo" */ '../views/DuelJoinView.vue'),
  },
  {
    path: '/duelos/:id',
    name: 'duel',
    component: () => import(/* webpackChunkName: "duelo" */ '../views/DuelDetailView.vue'),
  },
  {
    path: '/ranking',
    name: 'ranking',
    // Telas secundárias em chunk próprio: quem só quer jogar não baixa o resto.
    component: () => import(/* webpackChunkName: "ranking" */ '../views/RankingView.vue'),
    meta: { label: 'Ranking', icon: 'trophy' },
  },
  {
    // Sem `meta.label`: o perfil saiu da barra de abas e virou o avatar do cabeçalho, que
    // é onde a identidade do jogador já estava sendo lida.
    path: '/perfil',
    name: 'profile',
    component: () => import(/* webpackChunkName: "perfil" */ '../views/ProfileView.vue'),
  },
  {
    // Sem `meta.label`: ajuste não é destino de navegação diária. Chega-se por dentro do
    // perfil, que é onde o jogador já vem se ajustar.
    path: '/configuracoes',
    name: 'settings',
    component: () => import(/* webpackChunkName: "perfil" */ '../views/SettingsView.vue'),
  },
  {
    // Sem `meta.label`: o convite é destino de link, não aba da barra.
    path: '/convite/:code',
    name: 'invite',
    component: () => import(/* webpackChunkName: "conta" */ '../views/InviteView.vue'),
  },
  {
    // O token vem no fragmento (`#...`), que o navegador não manda ao servidor.
    path: '/conta/redefinir',
    name: 'password-reset',
    component: () => import(/* webpackChunkName: "conta" */ '../views/PasswordView.vue'),
  },
  {
    path: '/conta/senha',
    name: 'password',
    component: () => import(/* webpackChunkName: "conta" */ '../views/PasswordView.vue'),
  },
  {
    path: '/conta',
    name: 'account',
    component: () => import(/* webpackChunkName: "conta" */ '../views/AuthView.vue'),
  },
  { path: '/:pathMatch(.*)*', redirect: '/hoje' },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

/**
 * Sair da tela de jogo fecha o fecho que estava aberto.
 *
 * O flag sobrevivia à navegação, e voltar para "/" — pelo mapa da trilha, por exemplo —
 * reabria um resultado que o jogador já tinha fechado.
 */
router.beforeEach((to, from) => {
  if (from.path === '/' && to.path !== '/') store.commit('setResultToShow', false);
  return true;
});

/**
 * Senha redefinida pelo painel trava o app na troca. A temporária passou pela mão do
 * operador, e seguir jogando com ela é seguir com uma senha que outra pessoa conhece. A
 * primeira carga, antes de `/me` responder, é coberta pelo `App.vue`.
 */
router.beforeEach((to) => {
  // O link do e-mail também resolve a obrigação, então ele passa.
  if (!store.getters.mustChangePassword || ['password', 'password-reset'].includes(to.name)) return true;
  return { name: 'password' };
});

/**
 * Duelo tem relógio, e ele não para porque a tela mudou: sair da partida é desistir dela.
 * Por isso a saída é interrompida aqui e o casco pergunta — nos outros modos, trocar de aba
 * não encerra nada, só entrar em outra partida encerra.
 */
router.beforeEach((to, from) => {
  if (from.path !== '/' || to.path === '/') return true;
  if (!store.getters.activeDuel || store.getters.isOver) return true;
  store.dispatch('requestExit', to.fullPath);
  return false;
});

// A entrada tem duas perguntas de uma vez só: como o app se parece, e o que ele é. Quem já
// respondeu as duas passa direto; quem respondeu uma responde só a que falta. Vale apenas
// na porta da frente — link de convite, de duelo ou de conta continua indo ao destino.
router.beforeEach((to) => {
  if (to.name !== 'today' && to.name !== 'game') return true;
  let seen = true;
  try {
    seen = window.localStorage.getItem(WELCOME_KEY) === '1';
  } catch {
    // Sem armazenamento, não há primeira visita para reconhecer: segue para o jogo.
  }
  return seen && !neverPickedTheme() ? true : { name: 'welcome' };
});

export default router;
